import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';

import type { MetadataField } from '@/model/Metadata.model';
import type { Sample, SampleMeasurement } from '@/model/Sample.model';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import InVivoError from '@/model/InVivoError.ts';

import type { SpreadsheetSettings, CustomColumnSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import type { HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import { spreadSheetValidationTooltip } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import NoDataCard from '@/components/NoDataCard';
import Button from '@/components/UI/Button';
import Header from '@/components/UI/Header';
import Link from '@/components/UI/Link';
import SubHeader from '@/components/UI/SubHeader';
import { ApiService } from '@/support/ApiService';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { useFetchEntity } from '@/support/Hooks/fetch';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { web as webRoute } from '@/support/route';
import { clearSessionStorage, errorToast } from '@/helpers';
import { featuresSelector } from '@/support/Selectors';
import { modalAction } from '@/utils/modal';

import {
  getBaseSettings,
  type ColumnWrapper,
  createUnitLockCells,
  generateColumnData,
  returnInitialMetaValues,
  generateSampleBulkUpdateList,
} from './BulkSampleEdit.utils';
import BulkSampleEditTable from './BulkSampleEditTable';

export interface CustomSample extends Omit<Sample, 'details'> {
  details: Record<string, SampleMeasurement>;
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  reasonForChange: features?.reason_for_change ?? false,
  plannedSamples: features?.planned_samples ?? false,
}));

const BulkSampleEdit: FC = () => {
  const navigate = useNavigate();
  const tableRef = useRef<HotTableRef>(null);

  const [data, setData] = useState<Array<CustomSample>>([]);
  // Handsontable's `cells` callback reads the latest rows without forcing `settings` to be
  // rebuilt (and the grid re-created) on every edit; kept in sync with `data` below.
  const dataRef = useRef<Array<CustomSample>>([]);
  dataRef.current = data;
  const [apiError, setApiError] = useState<Error | false>(false);
  const [metadata, setMetadata] = useState<Array<MetadataField>>([]);
  const [sampleTypeLookup, setSampleTypeLookup] = useState<Record<string, TeamGlossaryV1>>();
  const [loading, setLoading] = useState(true);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [columnData, updateColumnData] = useState<ColumnWrapper>({
    colHeaders: [],
    columns: [],
  });

  const dispatch = useDispatch();
  const { openModal, closeModal } = modalAction(dispatch);
  const isMounted = useMountedState();

  const { reasonForChange, plannedSamples } = useSelector(featureSelector);

  const { id: studyId } = useParams();
  const { selectedSamples } = useSelector(
    ({ samples: selectedSamples }: { samples: { selectedSamples: Array<Sample> } }) => selectedSamples
  );

  const { entity: sampleMetadata, entityLoading: sampleMetadataLoading } = useFetchEntity<Array<MetadataField>>({
    entityType: 'glossaryMeta',
    queryParams: { filter_type: 'sample_meta' },
  });

  const { loading: sampleTypesLoading, response: sampleTypesResponse } = useApiHook({
    endpoint: 'GET /api/v1/team-glossary',
    query: { group: 'samples', perPage: -1 },
    options: { onError: { toast: false, slug: 'bulk-sample-edit-sample-types-load' } },
  });

  const sampleTypeOptions =
    sampleTypesResponse?.type === 'success' ? (sampleTypesResponse.body?.data ?? []) : undefined;

  useEffect(() => {
    if (!sampleTypesLoading && _notNil(sampleTypeOptions)) {
      setSampleTypeLookup(
        sampleTypeOptions.reduce<Record<string, TeamGlossaryV1>>((acc, type) => {
          if (_notNil(type.title) && type.title !== '') {
            acc[type.title] = type;
          }
          return acc;
        }, {})
      );
    }
  }, [sampleTypesResponse]);

  const studyApiId = useMemo<string>(() => sessionStorage.getItem(`selectedStudyApiId${studyId}`) ?? '', [studyId]);

  const samples = useMemo<Array<CustomSample>>(() => {
    const sessionSamples = JSON.parse(
      sessionStorage.getItem(`selectedStudy${studyId}Samples`) ?? '[]'
    ) as Array<Sample>;
    const sourceSamples = _isEmpty(sessionSamples) ? selectedSamples : sessionSamples;

    return sourceSamples.map((sample) => ({
      ...sample,
      details: sample.details.reduce<Record<string, SampleMeasurement>>((acc, detail) => {
        if (_notNil(detail.key)) {
          acc[detail.key] = detail;
        }
        return acc;
      }, {}),
      meta: returnInitialMetaValues(sample.metadata),
    }));
  }, [studyId, selectedSamples]);

  // Snapshot of sample_id values as loaded from the API, keyed by sample api_id.
  // Used to short-circuit the uniqueness validator: a value that still matches the
  // server-persisted sample_id is trivially unique (the DB already accepted it), so
  // we can skip the HTTP /unique/{sampleId} call. This eliminates redundant requests
  // triggered by HoT's greedy validation passes on every cell edit / mount / render.
  const originalSampleIdMap = useMemo<Map<string, string>>(
    () => new Map(samples.map((sample) => [String(sample.api_id), sample.sample_id])),
    [samples]
  );
  const getOriginalSampleId = useCallback(
    (sampleApiId: string): string | undefined => originalSampleIdMap.get(sampleApiId),
    [originalSampleIdMap]
  );

  useEffect(() => {
    return () => {
      clearSessionStorage(`selectedStudy${studyId}Samples`, `/studies/${studyId}/samples/edit`)();
      clearSessionStorage(`selectedStudyApiId${studyId}`, `/studies/${studyId}/samples/edit`)();
    };
  }, [studyId]);

  useEffect(() => {
    const metadataMap = new Map();

    if (_isEmpty(metadata) && _notNil(sampleMetadata) && _notNil(samples) && _notNil(sampleTypeLookup)) {
      const existingMetadataIds = samples.reduce<Set<string>>((acc, sample) => {
        sample.metadata?.forEach((sampleMeta) => {
          const metaId = sampleMeta?.glossary_id ?? sampleMeta?.id;
          if (_notNil(metaId)) {
            acc.add(String(metaId));
          }
        });
        return acc;
      }, new Set());

      sampleMetadata.reduce((acc, meta) => {
        if (meta.required === true || existingMetadataIds.has(String(meta.id))) {
          acc.set(meta.id, {
            id: meta.id,
            glossary_id: meta.id,
            field_type: meta.field_type,
            options: meta.options,
            read_only: meta.read_only,
            title: meta.title,
            created_at: meta.created_at,
            updated_at: meta.updated_at,
            slug: meta.slug,
            active: true,
            required: meta.required,
          });
        }
        return acc;
      }, metadataMap);

      setMetadata(sampleMetadata.filter(({ id }) => !metadataMap.has(id)));

      const metaValues = [...(metadataMap?.values() ?? [])] as Array<MetadataField>;
      const sampleTypeForColumns = _isNotEmpty(data) ? sampleTypeLookup?.[data[0].type] : undefined;

      updateColumnData(generateColumnData(metaValues, sampleTypeForColumns, plannedSamples, getOriginalSampleId));
    }
  }, [sampleMetadata, sampleTypeLookup, plannedSamples, getOriginalSampleId]);

  const settings = useMemo<SpreadsheetSettings>(() => {
    const getSampleAtRow = (row: number) => dataRef.current[row];
    const columns = (columnData as ColumnWrapper).columns ?? [];
    return {
      ...getBaseSettings(plannedSamples, getOriginalSampleId),
      ...columnData,
      cells: createUnitLockCells(columns as Array<{ isUnitColumn?: boolean }>, getSampleAtRow),
    };
  }, [columnData, plannedSamples, getOriginalSampleId]);

  const linkedTooltipRenderer = useCallback(
    (
      td: HTMLTableCellElement,
      _row: number,
      _col: number,
      _prop: string | number,
      _value: unknown,
      cellProperties: object
    ): void => {
      const tooltipMessage = (cellProperties as Record<string, unknown>).linkedTooltipMessage as string | undefined;
      if (_notNil(tooltipMessage)) {
        td.setAttribute('data-tooltip-id', 'global-tooltip-id');
        td.setAttribute('data-tooltip-content', tooltipMessage);
      }
    },
    []
  );

  useEffect(() => {
    setData(structuredClone(samples));
    setLoading(false);
  }, [samples]);

  const saveSamples = async (reasonForChangeValue?: string) => {
    setLoading(true);
    const sampleUpdateList = generateSampleBulkUpdateList(
      samples,
      data,
      settings.columns as Array<CustomColumnSettings>
    );
    try {
      await ApiService.call({
        endpoint: 'PATCH /api/v1/studies/{studyId}/samples/bulk',
        path: { studyId: studyApiId },
        body: { samples: sampleUpdateList, reason_for_change: reasonForChangeValue },
        options: { onError: { toast: false, throw: true, capture: false, slug: 'bulk-sample-edit-save' } },
      });
      navigate(webRoute('studies.samples', { id: studyId }));
      toast.success(`Successfully added ${samples.length > 1 ? 'samples' : 'sample'}`);
    } catch (error) {
      errorToast('Failed to save samples');
      ExceptionHandler.captureException(
        new InVivoError('Could not save samples', { cause: error, slug: 'bulk-sample-edit-save' })
      );
      if (isMounted()) {
        setApiError(error as Error);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  const saveAction = async () => {
    if (reasonForChange) {
      openModal('BULK_REASON_FOR_CHANGE', {
        title: 'Sample information has changed',
        onClick: saveSamples,
        closeModal,
      });
    } else {
      await saveSamples();
    }
  };

  const handleSetMetadata = (newMetadata: Array<MetadataField>): void => {
    setMetadata(newMetadata);
  };

  const spreadSheet = (
    <>
      <div className="ow-spreadsheet-styles">
        <BulkSampleEditTable
          data={data as unknown as Array<Record<string, unknown>>}
          settings={settings}
          innerRef={tableRef as React.RefObject<HotTableRef>}
          metadata={metadata}
          setMetadata={handleSetMetadata}
          setInvalid={setIsInvalid}
          maxRows={data.length}
          customAfterRenderer={linkedTooltipRenderer}
        />
      </div>
      <div className="pa4">
        <Button
          onClick={saveAction}
          disabled={loading || isInvalid}
          tooltip={isInvalid ? spreadSheetValidationTooltip : undefined}
        >
          Save
        </Button>
        <Button plain className="ml3" onClick={() => navigate(webRoute('studies.samples', { id: studyId }))}>
          Cancel
        </Button>
      </div>
    </>
  );

  return (
    <div className="h-100">
      {apiError !== false && (
        <ApiErrorBanner
          className="mb4"
          title={'There was an error with your request'}
          text={
            'An error has occurred when submitting your request, please try again later. If this keeps occurring please contact support.'
          }
          error={apiError}
        />
      )}
      {loading || sampleMetadataLoading || sampleTypesLoading || _isEmpty(settings) ? (
        <div style={{ height: 650 }}>
          <Loading txt={`${loading || sampleMetadataLoading ? 'Updating sample...' : 'Loading...'}`} />
        </div>
      ) : (
        <>
          <SubHeader linkToText="Samples" link={webRoute('studies.samples', { id: studyId })} />
          <div className="ph4">
            <Header mainHeaderText="Edit Samples" />
          </div>
          {_isNotEmpty(settings) && _isNotEmpty(data) ? (
            spreadSheet
          ) : (
            <div className="ma4">
              <NoDataCard
                title="You have not selected any samples"
                text={'Please select samples from your study to proceed'}
                NoDataComponent={
                  <Link to={webRoute('studies.samples', { id: studyId })} className="link blue">
                    Go to Samples
                  </Link>
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BulkSampleEdit;
